import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { downloadFromApi } from '../lib/download.js';
import { httpError } from '../lib/http-error.js';
import { ClientSearchSelect } from '../components/ClientSearchSelect.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { DateInput } from '../components/DateInput.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';
import type { UserOutput } from '@1financial/shared';

/* ── Shared API shapes (server: apps/api/src/routes/reports.ts) ─────────── */

interface Client360Item {
  id: string; serviceType: string; status: string; dueDate: string | null;
  overdue: boolean; blocked: boolean; assignee1: string | null; assignee2: string | null;
}
interface Client360Report {
  client: { id: string; clientCode: string; name: string; company: string | null; status: string; email: string | null; phone: string | null; country: string | null; jurisdiction: string | null };
  complianceCompanies: Array<{ id: string; clientCode: string; name: string; company: string | null; jurisdiction: string | null; retainerWon: boolean; items: Client360Item[] }>;
  engagements: Array<{ id: string; title: string | null; type: string | null; status: string; checklist: Array<{ id: string; title: string; status: string }> }>;
  proposals: Array<{ id: string; subject: string; status: string; feeAmount: number | null; feeCurrency: string | null; createdAt: string }>;
  invoices: Array<{ id: string; invoiceNumber: string; status: string; total: number; paid: number; balance: number; currency: string; issueDate: string; dueDate: string }>;
}
interface ProductivityRow {
  user: { id: string; displayName: string; role: string };
  tasks: { created: number; completed: number; pending: number };
  time: { billable: number; nonBillable: number; total: number };
  daysWithoutTime: number;
  attendanceExceptions: number;
}
interface ProductivityReport {
  range: { from: string; to: string };
  basis: Record<string, string>;
  rows: ProductivityRow[];
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
  if (!res.ok) throw await httpError(res);
  const body = await res.json() as { ok: true; data: T };
  return body.data;
}

function money(amount: number, currency: string | null): string {
  const major = amount / 100;
  return `${currency ?? ''} ${major.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export function ReportsPage() {
  const [tab, setTab] = useUserPreference<'client360' | 'productivity'>('reports.tab', 'client360', {
    validate: (value) => value === 'client360' || value === 'productivity',
  });
  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Reports</h1>
          <p className="text-sm text-muted">Client 360 and staff productivity, with CSV export</p>
        </div>
        <div className="flex gap-2">
          {(['client360', 'productivity'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-sm px-3 py-1.5 rounded-lg border ${tab === t ? 'border-accent text-white bg-bg-hover' : 'border-border text-muted hover:text-white'}`}
            >
              {t === 'client360' ? 'Client 360' : 'Staff productivity'}
            </button>
          ))}
        </div>
      </header>
      {tab === 'client360' ? <Client360Section /> : <ProductivitySection />}
    </div>
  );
}

/* ── Report 1 — Client 360 ──────────────────────────────────────────────── */

function Client360Section() {
  const [clientId, setClientId] = useUserPreference('reports.client360.clientId', '');
  const report = useQuery({
    queryKey: ['report-client-360', clientId],
    enabled: !!clientId,
    queryFn: () => fetchJson<Client360Report>(`/v1/reports/client-360?clientId=${clientId}`),
  });

  function exportCsv() {
    downloadFromApi(`/v1/reports/client-360/export?clientId=${clientId}`, 'client-360.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }

  const data = report.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 flex-wrap items-start sm:items-center">
        <ClientSearchSelect
          value={clientId}
          onChange={(id) => setClientId(id)}
          placeholder="Search client by name, company or code…"
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
          wrapperClassName="w-full sm:w-96"
        />
        {clientId && (
          <button onClick={exportCsv} className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg">Export CSV</button>
        )}
      </div>

      {!clientId && <div className="bg-bg-panel border border-border rounded-xl px-4 py-10 text-center text-sm text-muted">Pick a client to build its 360 report.</div>}
      {report.isLoading && clientId && <div className="bg-bg-panel border border-border rounded-xl px-4 py-10 text-center text-sm text-muted">Loading…</div>}
      {report.error && <div className="bg-bg-panel border border-border rounded-xl px-4 py-6 text-center text-sm text-rose-400">{(report.error as Error).message}</div>}

      {data && (
        <>
          <div className="bg-bg-panel border border-border rounded-xl p-4">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-base font-semibold text-white">{data.client.name}</span>
              <span className="text-xs text-muted">{data.client.clientCode}</span>
              <span className="text-xs text-muted">{data.client.status}</span>
              {data.client.company && <span className="text-xs text-muted">{data.client.company}</span>}
              {data.client.jurisdiction && <span className="text-xs text-muted">{data.client.jurisdiction}</span>}
            </div>
          </div>

          {data.complianceCompanies.map((company) => (
            <section key={company.id} className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
              <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium flex flex-wrap gap-x-3">
                <span className="text-white">{company.name}</span>
                <span>{company.clientCode}</span>
                {company.company && <span>{company.company}</span>}
                {company.retainerWon && <span className="text-emerald-300">retainer</span>}
                <span className="ml-auto">{company.items.length} item(s)</span>
              </div>
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-xs text-muted border-b border-border">
                    <th className="px-4 py-2 font-medium">Service</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Due</th>
                    <th className="px-4 py-2 font-medium">Flags</th>
                    <th className="px-4 py-2 font-medium">Assignees</th>
                  </tr>
                </thead>
                <tbody>
                  {company.items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted">No compliance items.</td></tr>}
                  {company.items.map((item) => (
                    <tr key={item.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                      <td className="px-4 py-2.5 text-white">{item.serviceType}</td>
                      <td className="px-4 py-2.5 text-muted">{item.status}</td>
                      <td className={`px-4 py-2.5 ${item.overdue ? 'text-rose-300' : 'text-white'}`}>{fmtDate(item.dueDate)}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {item.overdue && <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 mr-1">overdue</span>}
                        {item.blocked && <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300">blocked</span>}
                      </td>
                      <td className="px-4 py-2.5 text-muted">{[item.assignee1, item.assignee2].filter(Boolean).join(' & ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
          {data.complianceCompanies.length === 0 && (
            <div className="bg-bg-panel border border-border rounded-xl px-4 py-6 text-center text-sm text-muted">No compliance companies share this client code.</div>
          )}

          <section className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
            <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium">Engagements ({data.engagements.length})</div>
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="px-4 py-2 font-medium">Engagement</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Checklist</th>
                </tr>
              </thead>
              <tbody>
                {data.engagements.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-muted">No engagements.</td></tr>}
                {data.engagements.map((e) => (
                  <tr key={e.id} className="border-b border-border/50 hover:bg-bg-hover/40 align-top">
                    <td className="px-4 py-2.5 text-white">{e.title ?? '—'}{e.type && <span className="text-xs text-muted ml-2">{e.type}</span>}</td>
                    <td className="px-4 py-2.5 text-muted">{e.status}</td>
                    <td className="px-4 py-2.5">
                      {e.checklist.length === 0 && <span className="text-muted">—</span>}
                      <ul className="space-y-1">
                        {e.checklist.map((c) => (
                          <li key={c.id} className="flex items-center gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded ${c.status === 'COMPLETED' ? 'bg-emerald-500/15 text-emerald-300' : c.status === 'IN_PROCESS' ? 'bg-amber-500/15 text-amber-300' : 'bg-bg-hover text-muted'}`}>{c.status}</span>
                            <span className="text-white">{c.title}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
            <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium">Open proposals ({data.proposals.length})</div>
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="px-4 py-2 font-medium">Subject</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {data.proposals.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted">No open proposals.</td></tr>}
                {data.proposals.map((p) => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                    <td className="px-4 py-2.5 text-white">{p.subject}</td>
                    <td className="px-4 py-2.5 text-muted">{p.status}</td>
                    <td className="px-4 py-2.5 text-white">{p.feeAmount != null ? money(p.feeAmount, p.feeCurrency) : '—'}</td>
                    <td className="px-4 py-2.5 text-muted">{fmtDate(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
            <div className="px-4 py-3 bg-bg-card border-b border-border text-xs text-muted font-medium">Unpaid & partially paid invoices ({data.invoices.length})</div>
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-muted border-b border-border">
                  <th className="px-4 py-2 font-medium">Invoice</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Total</th>
                  <th className="px-4 py-2 font-medium">Paid</th>
                  <th className="px-4 py-2 font-medium">Balance</th>
                  <th className="px-4 py-2 font-medium">Due</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted">Nothing outstanding.</td></tr>}
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                    <td className="px-4 py-2.5 text-white">{inv.invoiceNumber}</td>
                    <td className="px-4 py-2.5 text-muted">{inv.status}</td>
                    <td className="px-4 py-2.5 text-white">{money(inv.total, inv.currency)}</td>
                    <td className="px-4 py-2.5 text-muted">{money(inv.paid, inv.currency)}</td>
                    <td className="px-4 py-2.5 text-white">{money(inv.balance, inv.currency)}</td>
                    <td className="px-4 py-2.5 text-muted">{fmtDate(inv.dueDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

/* ── Report 2 — Staff productivity ─────────────────────────────────────── */

type Preset = 'week' | 'month' | 'custom';

function presetRange(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  if (preset === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
  // this week: Monday → today
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return { from: iso(monday), to: iso(now) };
}

function ProductivitySection() {
  const [preset, setPreset] = useUserPreference<Preset>('reports.productivity.preset', 'month', {
    validate: (value) => value === 'week' || value === 'month' || value === 'custom',
  });
  const [customRange, setRange] = useUserPreference('reports.productivity.customRange', presetRange('month'), {
    validate: (value) => !!value && typeof value === 'object'
      && typeof (value as Record<string, unknown>).from === 'string'
      && typeof (value as Record<string, unknown>).to === 'string',
  });
  const range = preset === 'custom' ? customRange : presetRange(preset);
  const [userId, setUserId] = useUserPreference('reports.productivity.userId', '');

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p));
  };

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams({ from: range.from, to: range.to });
    if (userId) p.set('userId', userId);
    return p.toString();
  }, [range.from, range.to, userId]);

  const report = useQuery({
    queryKey: ['report-staff-productivity', queryString],
    queryFn: () => fetchJson<ProductivityReport>(`/v1/reports/staff-productivity?${queryString}`),
  });

  function exportCsv() {
    downloadFromApi(`/v1/reports/staff-productivity/export?${queryString}`, 'staff-productivity.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 flex-wrap sm:items-center">
        <div className="flex gap-1">
          {(['week', 'month', 'custom'] as const).map((p) => (
            <button
              key={p}
              onClick={() => applyPreset(p)}
              className={`text-xs px-3 py-1.5 rounded-lg border ${preset === p ? 'border-accent text-white bg-bg-hover' : 'border-border text-muted hover:text-white'}`}
            >
              {p === 'week' ? 'This week' : p === 'month' ? 'This month' : 'Custom'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted">From</label>
          <DateInput
            value={range.from}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
            className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted">To</label>
          <DateInput
            value={range.to}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
            className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white"
          />
        </div>
        <SearchableSelect
          value={userId}
          onChange={setUserId}
          placeholder="All staff"
          options={(users.data ?? []).filter((u) => u.status !== 'DISABLED' && u.role !== 'CLIENT').map((u) => ({ value: u.id, label: u.displayName }))}
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-accent"
          wrapperClassName="w-full sm:w-52"
        />
        <button onClick={exportCsv} className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg">Export CSV</button>
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Staff</th>
              <th className="px-4 py-3 font-medium text-right">Tasks created</th>
              <th className="px-4 py-3 font-medium text-right">Tasks completed</th>
              <th className="px-4 py-3 font-medium text-right">Tasks pending</th>
              <th className="px-4 py-3 font-medium text-right">Billable h</th>
              <th className="px-4 py-3 font-medium text-right">Non-billable h</th>
              <th className="px-4 py-3 font-medium text-right">Days w/o time</th>
              <th className="px-4 py-3 font-medium text-right">Missing checkout</th>
            </tr>
          </thead>
          <tbody>
            {report.isLoading && <TableSkeletonRows colSpan={8} />}
            {report.error && <tr><td colSpan={8} className="px-4 py-8 text-center text-rose-400">{(report.error as Error).message}</td></tr>}
            {report.data?.rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted">No staff in range.</td></tr>}
            {report.data?.rows.map((r) => (
              <tr key={r.user.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3 text-white">{r.user.displayName} <span className="text-xs text-muted">{r.user.role}</span></td>
                <td className="px-4 py-3 text-right text-muted">{r.tasks.created}</td>
                <td className="px-4 py-3 text-right text-muted">{r.tasks.completed}</td>
                <td className="px-4 py-3 text-right text-muted">{r.tasks.pending}</td>
                <td className="px-4 py-3 text-right text-white">{r.time.billable.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-muted">{r.time.nonBillable.toFixed(2)}</td>
                <td className={`px-4 py-3 text-right ${r.daysWithoutTime > 0 ? 'text-amber-300' : 'text-muted'}`}>{r.daysWithoutTime}</td>
                <td className={`px-4 py-3 text-right ${r.attendanceExceptions > 0 ? 'text-rose-300' : 'text-muted'}`}>{r.attendanceExceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {report.data && (
        <p className="text-xs text-muted">
          Engagement checklist items are attributed to the engagement manager/agent and counted by current status (no per-item completion timestamp exists). Days without time = Mon–Fri minus approved leave minus days with any time entry.
        </p>
      )}
    </div>
  );
}
